/**
 * Fisheye dewarp (ENG-2761): the fish2pano flag string staff store in
 * `Camera.panorama_parameters`, and an approximate preview of what the
 * connector will make of a raw fisheye frame.
 *
 * The connector runs Paul Bourke's `fish2pano` with these flags
 * (`[fish2pano] + shlex.split(panorama_parameters) + [input]`), so a bad
 * token breaks frame capture for that camera. The parser below is the same
 * grammar as actuate_admin's `validate_panorama_parameters`
 * (inframap/camera/camera_setup/panorama_parameters.py): the editor rejects
 * exactly what the PATCH would, with the same wording.
 *
 * Everything here is pure (no DOM, no canvas): the tab draws the result.
 */

// ---------------------------------------------------------------------------
// Grammar
// ---------------------------------------------------------------------------

export type PanoFlag =
  | "-w"
  | "-h"
  | "-s"
  | "-c"
  | "-r"
  | "-ry"
  | "-x"
  | "-y"
  | "-z"
  | "-a"
  | "-v"
  | "-la"
  | "-lo"
  | "-p"
  | "-2";

/** Each flag's values, in order; `-2` is present as `[]`. */
export type PanoParams = Partial<Record<PanoFlag, number[]>>;

/** Same as Camera.panorama_parameters.max_length. */
export const PANORAMA_MAX_LENGTH = 200;

const INT_RE = /^-?\d+$/;
// Accepts "-0", "-60", "12.5", "12.", ".5"; no exponents, no "+".
const NUMBER_RE = /^-?(\d+(\.\d*)?|\.\d+)$/;

interface ArgSpec {
  kind: "int" | "number";
  check?: (value: number) => boolean;
  requirement?: string;
}

interface FlagSpec {
  args: ArgSpec[];
  pairCheck?: (values: number[]) => boolean;
  pairRequirement?: string;
}

const positiveInt: ArgSpec = {
  kind: "int",
  check: (v) => v > 0,
  requirement: "a whole number greater than 0",
};
const anyNumber: ArgSpec = { kind: "number" };
const nonNegativeInt: ArgSpec = {
  kind: "int",
  check: (v) => v >= 0,
  requirement: "a whole number of 0 or more",
};

const FLAG_SPECS: Record<PanoFlag, FlagSpec> = {
  "-w": { args: [positiveInt] },
  "-h": { args: [positiveInt] },
  "-s": {
    args: [
      {
        kind: "number",
        check: (v) => v > 0,
        requirement: "a number greater than 0",
      },
    ],
  },
  "-c": { args: [nonNegativeInt, nonNegativeInt] },
  "-r": { args: [positiveInt] },
  "-ry": { args: [positiveInt] },
  "-x": { args: [anyNumber] },
  "-y": { args: [anyNumber] },
  "-z": { args: [anyNumber] },
  "-a": {
    args: [
      {
        kind: "int",
        check: (v) => v >= 1 && v <= 9,
        requirement: "a whole number from 1 to 9",
      },
    ],
  },
  "-v": {
    args: [
      {
        kind: "number",
        check: (v) => v > 0,
        requirement: "a number greater than 0",
      },
    ],
  },
  "-la": {
    args: [anyNumber, anyNumber],
    pairCheck: (vals) => vals[0] < vals[1],
    pairRequirement: "the first latitude must be less than the second",
  },
  "-lo": {
    args: [anyNumber, anyNumber],
    pairCheck: (vals) => vals[0] !== vals[1],
    pairRequirement: "the two longitudes must differ",
  },
  "-p": { args: [anyNumber, anyNumber, anyNumber, anyNumber] },
  "-2": { args: [] },
};

/** Flags that write files or debug output: the connector owns those. */
const FORBIDDEN_FLAGS: Record<string, string> = {
  "-f": "writes to a file",
  "-o": "sets the output file",
  "-d": "enables debug output",
};

/** Order the serializer writes flags in (the census strings already use it). */
export const FLAG_ORDER: PanoFlag[] = [
  "-w",
  "-h",
  "-s",
  "-c",
  "-r",
  "-ry",
  "-x",
  "-y",
  "-la",
  "-lo",
  "-z",
  "-a",
  "-v",
  "-p",
  "-2",
];

const isFlag = (token: string): token is PanoFlag =>
  Object.prototype.hasOwnProperty.call(FLAG_SPECS, token);

const argCount = (flag: PanoFlag) => {
  const n = FLAG_SPECS[flag].args.length;
  return n === 1 ? `${n} value` : `${n} values`;
};

export type ParseResult =
  | { ok: true; params: PanoParams; normalized: string }
  | { ok: false; error: string };

class PanoError extends Error {}

const parseValue = (
  flag: PanoFlag,
  arg: ArgSpec,
  token: string | undefined
): number => {
  if (token === undefined)
    throw new PanoError(
      `${flag} needs ${argCount(flag)}, but the parameters end too early.`
    );
  const pattern = arg.kind === "int" ? INT_RE : NUMBER_RE;
  if (!pattern.test(token))
    throw new PanoError(
      `${flag} needs ${
        arg.kind === "int" ? "a whole number" : "a number"
      }, got '${token}'.`
    );
  const value = Number(token);
  if (arg.check && !arg.check(value))
    throw new PanoError(`${flag} must be ${arg.requirement}, got '${token}'.`);
  return value;
};

const unknownFlagError = (token: string): string => {
  if (FORBIDDEN_FLAGS[token])
    return `${token} is not allowed: it ${FORBIDDEN_FLAGS[token]}, which the connector manages.`;
  if (!token.startsWith("-") && isFlag(`-${token}`))
    return `'${token}' is not a fish2pano flag. Did you mean '-${token}'?`;
  if (token.startsWith("-"))
    return `'${token}' is not a supported fish2pano flag.`;
  return `Expected a fish2pano flag (such as -w), got '${token}'.`;
};

const previousFlag = (tokens: string[], index: number): PanoFlag => {
  for (let i = index - 1; i >= 0; i -= 1) {
    if (isFlag(tokens[i])) return tokens[i] as PanoFlag;
  }
  return tokens[0] as PanoFlag;
};

/**
 * Parse a fish2pano flag string. Empty or whitespace means "no panorama"
 * (`ok`, no flags, normalized ""), which is what a PATCH of '' stores.
 */
export const parsePanoramaParameters = (raw: string | null): ParseResult => {
  const tokens = (raw ?? "").split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { ok: true, params: {}, normalized: "" };
  const normalized = tokens.join(" ");
  if (normalized.length > PANORAMA_MAX_LENGTH)
    return {
      ok: false,
      error: `Panorama parameters must be at most ${PANORAMA_MAX_LENGTH} characters (got ${normalized.length}).`,
    };
  const params: PanoParams = {};
  try {
    let i = 0;
    while (i < tokens.length) {
      const token = tokens[i];
      if (!isFlag(token)) {
        if (i > 0 && NUMBER_RE.test(token)) {
          const previous = previousFlag(tokens, i);
          throw new PanoError(
            `Unexpected extra value '${token}' after ${previous} (it takes ${argCount(
              previous
            )}).`
          );
        }
        throw new PanoError(unknownFlagError(token));
      }
      if (params[token])
        throw new PanoError(`${token} is given more than once.`);
      const spec = FLAG_SPECS[token];
      const values = spec.args.map((arg, offset) =>
        parseValue(token, arg, tokens[i + 1 + offset])
      );
      if (spec.pairCheck && !spec.pairCheck(values))
        throw new PanoError(`${token}: ${spec.pairRequirement}.`);
      params[token] = values;
      i += 1 + spec.args.length;
    }
  } catch (error) {
    if (error instanceof PanoError) return { ok: false, error: error.message };
    throw error;
  }
  return { ok: true, params, normalized };
};

const formatNumber = (value: number): string =>
  Object.is(value, -0) ? "0" : String(value);

/** Flags in FLAG_ORDER, one space apart. */
export const serializePanoramaParameters = (params: PanoParams): string =>
  FLAG_ORDER.filter((flag) => params[flag] !== undefined)
    .map((flag) =>
      [flag, ...(params[flag] as number[]).map(formatNumber)].join(" ")
    )
    .join(" ");

/** Whitespace-normalized text, for "did this change?". */
export const normalizePanoramaText = (raw: string | null): string =>
  (raw ?? "").split(/\s+/).filter(Boolean).join(" ");

// ---------------------------------------------------------------------------
// Styles and presets
// ---------------------------------------------------------------------------

/**
 * "band": the 360° latitude band (`-la`), usually split in two (`-2`).
 * "arc": a partial arc set by the lens field of view (`-s`), no `-la`.
 */
export type PanoStyle = "band" | "arc";

export const panoStyleOf = (params: PanoParams): PanoStyle =>
  params["-la"] ? "band" : "arc";

/** The tilt every census string uses; shown, never edited. */
export const FIXED_TILT = 180;

/** Switch style, keeping what both styles share (centre, radius, pan, antialias). */
export const switchPanoStyle = (
  params: PanoParams,
  style: PanoStyle
): PanoParams => {
  const next: PanoParams = { ...params, "-x": [FIXED_TILT] };
  if (style === "band") {
    delete next["-s"];
    delete next["-lo"];
    next["-w"] = [3000];
    next["-la"] = params["-la"] ?? [-60, -10];
    next["-2"] = [];
  } else {
    delete next["-la"];
    delete next["-2"];
    next["-w"] = [800];
    next["-s"] = params["-s"] ?? [240];
  }
  return next;
};

export interface PanoPreset {
  label: string;
  value: string;
}

/** The common values in production (2026-10 census) and the ENG-2761 request. */
export const PANORAMA_PRESETS: PanoPreset[] = [
  {
    label: "360° split, band −83…−10, pan 70",
    value: "-w 3000 -x 180 -la -83 -10 -z 70 -2",
  },
  { label: "360° split, band −80…−10", value: "-w 3000 -x 180 -la -80 -10 -2" },
  { label: "360° split, band −60…−10", value: "-w 3000 -x 180 -la -60 -10 -2" },
  { label: "360° split, band −60…−5", value: "-w 3000 -x 180 -la -60 -5 -2" },
  { label: "360° split, band −50…0", value: "-w 3000 -x 180 -la -50 0 -2" },
  { label: "360° split, band −65…0", value: "-w 3000 -x 180 -la -65 0 -2" },
  { label: "Arc, 240° lens", value: "-w 800 -s 240 -x 180" },
  {
    label: "Arc, 240° lens, longitude 120…240",
    value: "-w 800 -s 240 -x 180 -lo 120 240",
  },
  {
    label: "Arc, 200° lens, radius 400, longitude 120…300",
    value: "-w 800 -s 200 -r 400 -x 180 -lo 120 300",
  },
  {
    label: "Arc, 250° lens, longitude 100…280, wide",
    value: "-w 1200 -s 250 -x 180 -lo 100 280",
  },
];

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/** An RGBA pixel buffer; ImageData fits it, and so does a plain object in tests. */
export interface Raster {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

/** fish2pano's default output width. */
export const DEFAULT_PANO_WIDTH = 1024;

export interface PanoLayout {
  style: PanoStyle;
  /** The connector's panorama width (`-w`) before any split. */
  panoWidth: number;
  /** Width actually rendered (≤ panoWidth); `scale` = renderWidth / panoWidth. */
  renderWidth: number;
  scale: number;
  /** Rows of the (unsplit) panorama at render size. */
  bandHeight: number;
  split: boolean;
  /** The image drawn: halves stacked when split. */
  outWidth: number;
  outHeight: number;
}

const rad = (deg: number) => (deg * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Longitude span of `-lo`, wrapping a decreasing pair through 360. */
const lonSpanOf = (lo: number[] | undefined): number => {
  if (!lo) return 360;
  const span = lo[1] - lo[0];
  return span > 0 ? span : span + 360;
};

/**
 * Output size for these flags, rendered no wider than `maxWidth`.
 *
 * Band style is the actuate-pullers port, exact for `-w W -x 180 -la A B
 * [-2]`: height `int(W/π·tan(band/2))`. Arc style has no reference, so its
 * height keeps pixels square: `W · (fov/2) / longitude span`. `-h`, when
 * given, wins in both. null when the flags describe no image (an empty or
 * ≥180° band).
 */
export const panoramaLayout = (
  params: PanoParams,
  maxWidth = Infinity
): PanoLayout | null => {
  const panoWidth = params["-w"]?.[0] ?? DEFAULT_PANO_WIDTH;
  const renderWidth = Math.max(2, Math.min(panoWidth, Math.floor(maxWidth)));
  const scale = renderWidth / panoWidth;
  const style = panoStyleOf(params);
  const split = params["-2"] !== undefined;
  let bandHeight: number;
  if (params["-h"]) {
    bandHeight = Math.round(params["-h"][0] * scale);
  } else if (style === "band") {
    const [top, bottom] = params["-la"] as number[];
    const band = bottom - top;
    if (!(band > 0 && band < 180)) return null;
    bandHeight = Math.floor((renderWidth / Math.PI) * Math.tan(rad(band) / 2));
  } else {
    const fov = params["-s"]?.[0] ?? 180;
    bandHeight = Math.round(
      (renderWidth * (fov / 2)) / lonSpanOf(params["-lo"])
    );
  }
  if (bandHeight < 1) return null;
  const outWidth = split ? Math.floor(renderWidth / 2) : renderWidth;
  return {
    style,
    panoWidth,
    renderWidth,
    scale,
    bandHeight,
    split,
    outWidth,
    outHeight: split ? bandHeight * 2 : bandHeight,
  };
};

/** The fisheye circle in source pixels. */
export interface FisheyeLens {
  cx: number;
  cy: number;
  /** Radius of the full field of view (horizontal, and vertical for -ry). */
  r: number;
  ry: number;
  /** Lens field of view in degrees (`-s`, default 180). */
  fov: number;
}

/**
 * The lens for a source image. `sourceScale` is how much the frame was
 * shrunk before sampling (pixel flags -c/-r/-ry are in full-size pixels).
 * Defaults: centre = image centre, radius = min(w, h) / 2.
 */
export const fisheyeLens = (
  params: PanoParams,
  srcWidth: number,
  srcHeight: number,
  sourceScale = 1
): FisheyeLens => {
  const c = params["-c"];
  const r = params["-r"]
    ? params["-r"][0] * sourceScale
    : Math.min(srcWidth, srcHeight) / 2;
  return {
    cx: c ? c[0] * sourceScale : srcWidth / 2,
    cy: c ? c[1] * sourceScale : srcHeight / 2,
    r,
    ry: params["-ry"] ? params["-ry"][0] * sourceScale : r,
    fov: params["-s"]?.[0] ?? 180,
  };
};

/**
 * Equidistant fisheye: latitude −90 is the image centre, and the radius
 * grows linearly to the lens edge at `fov/2 − 90`. Longitude 0 points down
 * the image (azimuth = longitude + 90°), as in the actuate-pullers port.
 */
export const fisheyePoint = (
  lat: number,
  lon: number,
  lens: FisheyeLens
): [number, number] => {
  const k = (90 + lat) / (lens.fov / 2);
  const az = rad(lon + 90);
  return [
    lens.cx + lens.r * k * Math.cos(az),
    lens.cy + lens.ry * k * Math.sin(az),
  ];
};

/**
 * Latitude and longitude of panorama pixel (col, row), both at render size.
 *
 * Longitude: `-lo` start (0) + (col + .5)/W · span (360) + `-z` pan.
 * Band rows follow the pullers port (a tangent spacing around the band
 * centre). Arc rows run linearly from the lens edge (`fov/2 − 90`, top) to
 * the nadir (−90, bottom).
 */
export const panoramaAngles = (
  col: number,
  row: number,
  params: PanoParams,
  layout: PanoLayout
): [number, number] => {
  const lo = params["-lo"];
  const lon =
    (lo ? lo[0] : 0) +
    ((col + 0.5) / layout.renderWidth) * lonSpanOf(lo) +
    (params["-z"]?.[0] ?? 0);
  let lat: number;
  if (params["-la"]) {
    const [top, bottom] = params["-la"];
    const y =
      (layout.bandHeight / 2 - (row + 0.5)) *
      ((2 * Math.PI) / layout.renderWidth);
    lat = (top + bottom) / 2 + deg(Math.atan(y));
  } else {
    const fov = params["-s"]?.[0] ?? 180;
    const latTop = fov / 2 - 90;
    lat = latTop - ((row + 0.5) / layout.bandHeight) * (latTop + 90);
  }
  return [lat, lon];
};

const OUTSIDE = [17, 24, 39]; // slate-900: outside the fisheye circle

const sampleBilinear = (
  src: Raster,
  x: number,
  y: number,
  out: Uint8ClampedArray,
  o: number
) => {
  const { width, height, data } = src;
  if (!(x >= 0 && y >= 0 && x <= width - 1 && y <= height - 1)) {
    out[o] = OUTSIDE[0];
    out[o + 1] = OUTSIDE[1];
    out[o + 2] = OUTSIDE[2];
    out[o + 3] = 255;
    return;
  }
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(x0 + 1, width - 1);
  const y1 = Math.min(y0 + 1, height - 1);
  const fx = x - x0;
  const fy = y - y0;
  const i00 = (y0 * width + x0) * 4;
  const i10 = (y0 * width + x1) * 4;
  const i01 = (y1 * width + x0) * 4;
  const i11 = (y1 * width + x1) * 4;
  for (let c = 0; c < 3; c += 1) {
    const top = data[i00 + c] * (1 - fx) + data[i10 + c] * fx;
    const bottom = data[i01 + c] * (1 - fx) + data[i11 + c] * fx;
    out[o + c] = top * (1 - fy) + bottom * fy;
  }
  out[o + 3] = 255;
};

/**
 * Render the panorama by inverse mapping every output pixel to the fisheye
 * (bilinear). With `-2` the panorama is cut into left and right halves and
 * the RIGHT half is stacked on top of the LEFT, as fish2pano does.
 */
export const dewarpFisheye = (
  src: Raster,
  params: PanoParams,
  options: { maxWidth?: number; sourceScale?: number } = {}
): { image: Raster; layout: PanoLayout } | null => {
  const layout = panoramaLayout(params, options.maxWidth ?? Infinity);
  if (!layout) return null;
  const lens = fisheyeLens(
    params,
    src.width,
    src.height,
    options.sourceScale ?? 1
  );
  const { outWidth, outHeight, bandHeight, split } = layout;
  const data = new Uint8ClampedArray(outWidth * outHeight * 4);
  for (let oy = 0; oy < outHeight; oy += 1) {
    const top = split && oy < bandHeight;
    const row = split && !top ? oy - bandHeight : oy;
    for (let ox = 0; ox < outWidth; ox += 1) {
      const col = top ? ox + outWidth : ox;
      const [lat, lon] = panoramaAngles(col, row, params, layout);
      const [sx, sy] = fisheyePoint(lat, lon, lens);
      sampleBilinear(src, sx, sy, data, (oy * outWidth + ox) * 4);
    }
  }
  return { image: { width: outWidth, height: outHeight, data }, layout };
};

// ---------------------------------------------------------------------------
// Sample frame
// ---------------------------------------------------------------------------

const hslToRgb = (
  h: number,
  s: number,
  l: number
): [number, number, number] => {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [f(0) * 255, f(8) * 255, f(4) * 255];
};

/**
 * A synthetic 180° equidistant fisheye for the sample preview: a coloured
 * sector every 30° of longitude (white at 0°, where the 360° seam is), a
 * dark ring every 10° of latitude (heavier every 30°). Dewarped correctly
 * the sectors become vertical stripes and the rings horizontal lines.
 */
export const fisheyeTestPattern = (size: number): Raster => {
  const data = new Uint8ClampedArray(size * size * 4);
  const c = size / 2;
  const radius = size / 2;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const o = (y * size + x) * 4;
      const dx = x - c;
      const dy = y - c;
      const rr = Math.sqrt(dx * dx + dy * dy);
      data[o + 3] = 255;
      if (rr > radius) {
        data[o] = 8;
        data[o + 1] = 8;
        data[o + 2] = 8;
        continue;
      }
      const lat = (rr / radius) * 90 - 90;
      const lon = (((deg(Math.atan2(dy, dx)) - 90) % 360) + 360) % 360;
      const sector = Math.floor(lon / 30);
      let rgb: [number, number, number] =
        sector === 0
          ? [245, 245, 245]
          : hslToRgb(sector * 30, 0.65, sector % 2 ? 0.55 : 0.68);
      const latLine = Math.abs(lat - Math.round(lat / 10) * 10);
      const heavy = Math.round(lat / 10) % 3 === 0;
      const lonLine = Math.abs(lon - Math.round(lon / 30) * 30);
      if (latLine < (heavy ? 1.1 : 0.5) || (lonLine < 0.6 && lat > -85)) {
        rgb = [20, 25, 40];
      }
      data[o] = rgb[0];
      data[o + 1] = rgb[1];
      data[o + 2] = rgb[2];
    }
  }
  return { width: size, height: size, data };
};
